import { pageSchemaScript } from "@/seo/schema";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SECTION_OG, sectionImageMeta } from "@/lib/ogSections";
import { PageShell, PageHero } from "@/components/PageShell";
import { SectionHeader } from "@/components/SectionHeader";
import { Reveal } from "@/components/Reveal";
import { AuthorCard } from "@/components/AuthorCard";
import { BookOpen, Calculator, GitCompare, AlertTriangle, ListChecks, ShoppingBag, ArrowRight } from "lucide-react";

const CATEGORIES = [
  { Icon: BookOpen, title: "Foundational Concepts", count: "Core knowledge", desc: "How CCTV, access control, perimeter, fire alarm, automation, AV and cinema systems actually work." },
  { Icon: Calculator, title: "Cost Guides", count: "Indicative budgets", desc: "Realistic budgets for security, automation, cinema and AV programs in India." },
  { Icon: GitCompare, title: "Comparisons", count: "Side-by-side", desc: "Vendor-neutral evaluations across approaches, technologies and platforms." },
  { Icon: AlertTriangle, title: "Mistakes to Avoid", count: "Lessons from the field", desc: "Costly errors we see repeatedly — and how to prevent them at design stage." },
  { Icon: ListChecks, title: "Planning Checklists", count: "Step-by-step", desc: "Pre-design checklists for every program type — residence, corporate, hospitality, industrial." },
  { Icon: ShoppingBag, title: "Buying Guides", count: "Vendor-neutral", desc: "How to specify and procure systems across the building-technology stack." },
];

const FAQS = [
  { q: "What is electric fencing and how does it work?", a: "An electric fence delivers a short, safe pulse to deter intruders, paired with an energiser, sensors and an alarm system. Modern variants integrate with VMS and access control for unified perimeter defence." },
  { q: "How much CCTV storage is typically needed?", a: "Plan ~10–15 GB per camera per day at 4 MP / H.265 / 12 fps for 24×7. A 32-camera site retaining 30 days needs roughly 12–15 TB usable storage with appropriate RAID overhead." },
  { q: "What's the realistic cost of full smart-home automation?", a: "A meaningful integrated automation program for a luxury residence in India ranges ₹15–₹80 lakh depending on platform, scenes, lighting fixture count and AV integration." },
  { q: "How do I plan a home theatre room?", a: "Start with architecture: room ratio, isolation, riser, sightlines. Then specify display and audio for the calibrated room — not the other way around." },
  { q: "CCTV vs perimeter protection — what's the difference?", a: "CCTV records and verifies. Active perimeter detects intent at the boundary before intrusion. Mature programs use perimeter as the upstream layer and CCTV for verification and forensics." },
  { q: "What are the common smart-home mistakes?", a: "Brand-app sprawl, no scene engineering, ignored network and power design, and under-specified training for owners and staff." },
];

const LEARNING_TITLE = "Learning Centre — Security, Automation & AV | Arif Khan";
const LEARNING_DESC =
  "Learn practical concepts in security infrastructure, electronic security, smart automation, AV, home cinema, auditorium technology, and intelligent buildings.";

export const Route = createFileRoute("/insights/learning-centre")({
  component: LearningCentrePage,
  head: () => ({
    meta: [
      { title: LEARNING_TITLE },
      { name: "description", content: LEARNING_DESC },
      { property: "og:title", content: LEARNING_TITLE },
      { property: "og:description", content: "Practical learning on security, automation, AV, home cinema and intelligent buildings." },
      { property: "og:url", content: "https://arifkhanglobal.com/insights/learning-centre" },
      ...sectionImageMeta(SECTION_OG.askArif, "Learning centre — Arif Khan"),
    ],
    links: [
      { rel: "canonical", href: "https://arifkhanglobal.com/insights/learning-centre" },
    ],
    scripts: [
      pageSchemaScript(
        {
          path: "/insights/learning-centre",
          type: "CollectionPage",
          name: LEARNING_TITLE,
          description: LEARNING_DESC,
          breadcrumb: [
            { name: "Insights", url: "/insights" },
            { name: "Learning Centre", url: "/insights/learning-centre" },
          ],
        },
        [
          {
            "@type": "FAQPage",
            "@id": "https://arifkhanglobal.com/insights/learning-centre#faq",
            inLanguage: "en-IN",
            isPartOf: { "@id": "https://arifkhanglobal.com/insights/learning-centre#webpage" },
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ],
      ),
    ],

  }),
});

function LearningCentrePage() {
  return (
    <PageShell>
      <PageHero
        eyebrow="Learning Centre"
        title="A knowledge hub engineered for owners, architects and decision-makers."
        description="Foundational concepts, cost guides, comparisons and checklists across security infrastructure, electronic security, smart automation, AV, home cinema, auditorium technology and intelligent buildings."
      />

      <section className="py-24">
        <div className="container-page">
          <SectionHeader eyebrow="Sections" title="What you'll find inside." />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border rounded-2xl overflow-hidden">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.04}>
                <div className="bg-background p-8 h-full">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary mb-6">
                    <c.Icon className="h-5 w-5" strokeWidth={1.6} />
                  </div>
                  <h3 className="text-lg font-semibold">{c.title}</h3>
                  <p className="mt-2 text-sm text-ink-soft leading-relaxed">{c.desc}</p>
                  <div className="mt-6 text-xs font-mono text-primary uppercase tracking-widest">{c.count}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-surface border-y border-border">
        <div className="container-page max-w-3xl">
          <SectionHeader eyebrow="Frequently Asked" title="Direct answers." />
          <div className="mt-10 space-y-4">
            {FAQS.map((f, i) => (
              <Reveal key={i} delay={i * 0.03}>
                <details className="group rounded-2xl border border-border bg-background p-6">
                  <summary className="flex items-center justify-between cursor-pointer list-none gap-4">
                    <span className="font-semibold text-foreground">{f.q}</span>
                    <span className="text-primary transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                  </summary>
                  <p className="mt-4 text-ink-soft leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/ask-arif" className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
              Explore the full Ask Arif knowledge centre <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 border-t border-border">
        <div className="container-page max-w-4xl">
          <AuthorCard />
        </div>
      </section>
    </PageShell>
  );
}
